// src/context/CartContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      return []; // Always return array even on error
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('cart', JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cart]);

  const addToCart = (product, selectedOption = null) => {
    setCart(prevCart => {
      // Ensure prevCart is an array
      const safePrevCart = Array.isArray(prevCart) ? prevCart : [];
      
      const itemKey = selectedOption 
        ? `${product.id}-${selectedOption.option}`
        : `${product.id}`;
      
      const existingItemIndex = safePrevCart.findIndex(item => 
        item.key === itemKey
      );
      
      if (existingItemIndex >= 0) {
        const updatedCart = [...safePrevCart];
        updatedCart[existingItemIndex].quantity += 1;
        return updatedCart;
      } else {
        const newItem = {
          ...product,
          key: itemKey,
          selectedOption,
          displayName: selectedOption 
            ? `${product.name} (${selectedOption.option})`
            : product.name,
          price: selectedOption ? selectedOption.price : product.price,
          quantity: 1
        };
        return [...safePrevCart, newItem];
      }
    });
  };

  const updateQuantity = (itemKey, change) => {
    setCart(prevCart => {
      // Ensure prevCart is an array
      const safePrevCart = Array.isArray(prevCart) ? prevCart : [];
      
      const itemIndex = safePrevCart.findIndex(cartItem => cartItem.key === itemKey);
      
      if (itemIndex >= 0) {
        const updatedCart = [...safePrevCart];
        const newQuantity = updatedCart[itemIndex].quantity + change;
        
        if (newQuantity <= 0) {
          updatedCart.splice(itemIndex, 1);
        } else {
          updatedCart[itemIndex].quantity = newQuantity;
        }
        
        return updatedCart;
      }
      return safePrevCart;
    });
  };

  const removeFromCart = (itemKey) => {
    setCart(prevCart => {
      // Ensure prevCart is an array
      const safePrevCart = Array.isArray(prevCart) ? prevCart : [];
      return safePrevCart.filter(cartItem => cartItem.key !== itemKey);
    });
  };

  const clearCart = () => {
    setCart([]);
  };

  // Ensure cart is always an array before calculating
  const safeCart = Array.isArray(cart) ? cart : [];
  const cartTotal = safeCart.reduce((sum, item) => sum + ((item?.price || 0) * (item?.quantity || 0)), 0);
  const itemCount = safeCart.reduce((sum, item) => sum + (item?.quantity || 0), 0);

  return (
    <CartContext.Provider value={{
      cart: safeCart, // Always return an array
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      cartTotal,
      itemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};